/* eslint-env browser */
import * as d3 from 'd3';
import { Storage } from 'aws-amplify';

export const DATA_TYPE = {
  CSV: 'CSV',
  JSON: 'JSON',
};

const USER_VIDEO_FOLDER = 'videos-raw';

function getType(fileName) {
  if (fileName.endsWith('json')) {
    return DATA_TYPE.JSON;
  }
  if (fileName.endsWith('csv')) {
    return DATA_TYPE.CSV;
  }
  return null;
}

/**
 * List all objects in an S3 folder (handles pagination for Amplify v5+).
 * @param {Function} listFn - Storage.list or Storage.vault.list
 * @param {String} path - S3 path prefix (folder)
 * @param {Object} [options] - Optional parameters (e.g., level, pageSize)
 * @returns {Promise<Array>} - Array of all listed objects
 */
async function listAll(listFn, path, options = {}) {
  let nextToken;
  let allResults = [];

  do {
    // eslint-disable-next-line no-await-in-loop
    const { results = [], nextToken: token } = await listFn(path, { ...options, nextToken });
    allResults = allResults.concat(results);
    nextToken = token;
  } while (nextToken);

  return allResults;
}

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const convertBlobToBase64 = async (blob) => blobToBase64(blob);

// eslint-disable-next-line consistent-return
export async function listPublicReports(path = '') {
  try {
    const list = await listAll(Storage.list, `${path}`);
    return list.map((d) => d.key.split('/').pop()).filter((d) => !d.startsWith('.'));
  } catch (e) {
    console.error(e);
  }
}

// eslint-disable-next-line consistent-return
export async function getPublicReport(fileName, expectingDataType) {
  try {
    const data = await Storage.get(fileName, { download: true });
    const textData = await data.Body.text();
    const dataType = expectingDataType || getType(fileName);
    switch (dataType) {
      case DATA_TYPE.JSON:
        return JSON.parse(textData);
      case DATA_TYPE.CSV:
        return d3.csvParse(textData);
      default:
        return data;
    }
  } catch (e) {
    console.error(e);
  }
}

// it will always return the raw object please handle the Body property according to the type of data your expecting
// eslint-disable-next-line consistent-return
export async function getUserReport(fileName, expectingDataType, path) {
  // As long as there's one user signed in, Storage will handle credentials and uthenticantion
  // if (fileName) {
  try {
    // using vault is same as using the private path
    const data = await Storage.vault.get(`${path || 'report-data'}/${fileName}`, {
      download: true,
    });

    const textData = await data.Body.text();
    switch (expectingDataType) {
      case DATA_TYPE.JSON:
        return JSON.parse(textData);
      case DATA_TYPE.CSV:
        return d3.csvParse(textData);
      default:
        return textData;
    }
  } catch (e) {
    console.error(e);
  }
}

// eslint-disable-next-line consistent-return
export async function listUserReports(folderName, path) {
  try {
    const list = await listAll(Storage.vault.list, `${path || 'report-data'}/${folderName}`);
    return list.map((d) => d.key.split('/').pop()).filter((d) => !d.startsWith('.'));
  } catch (e) {
    console.error(e);
  }
}

// eslint-disable-next-line consistent-return
export async function getUserReportsInFolder(folderName, path) {
  try {
    const list = await listAll(Storage.vault.list, `${path || 'report-data'}/${folderName}`);
    const res = await Promise.all(
      list
        .map((d) => d.key.split('/').pop())
        .filter((d) => !d.startsWith('.'))
        .map((d) => getUserReport(`${folderName}${d}`, getType(d)))
    );
    return res;
  } catch (e) {
    console.error(e);
  }
}

// eslint-disable-next-line consistent-return
export async function getUserAvatar(fileName, path) {
  try {
    const data = await Storage.vault.get(`${path || 'avatar'}/${fileName || 'avatar.jpg'}`, {
      download: true,
    });
    return await convertBlobToBase64(data.Body);
  } catch (e) {
    console.error(e);
  }
}

export async function getVisionImage(strokeID, camera, event, type) {
  const path = strokeID.split('--');
  let data;
  if (type === '3D') {
    data = await Storage.vault.get(
      `vision-data/session-shots/${path[0]}/${path[1]}/${strokeID}.png`,
      { download: true }
    );
    return convertBlobToBase64(data.Body);
  }
  if (camera == null && event == null && type == null) {
    data = await Storage.vault.get(
      `vision-data/session-montages/${path[0]}/${path[1]}/${strokeID}.jpg`,
      { download: true }
    );
  } else if (type == null) {
    data = await Storage.vault.get(
      `vision-data/session-events/${path[0]}/${path[1]}/${camera}/${event}/${strokeID}.jpg`,
      { download: true }
    );
  }
  return convertBlobToBase64(data.Body);
}

export async function listUserVideoUploads(folderName = USER_VIDEO_FOLDER) {
  try {
    const uploads = await listAll(Storage.vault.list, folderName);
    return uploads
      .map((item) => item.key.split('/').pop())
      .filter((key) => !!key && key.endsWith('.json'))
      .map((key) => key.replace('.json', ''));
  } catch (error) {
    console.error(error);
    return [];
  }
}

const getFileSize = (file) => {
  if (!file) return 0;
  if (typeof file.size === 'number') return file.size;
  return new Blob([file]).size;
};

export async function uploadUserVideos({
  payload,
  jsonFileName,
  files,
  folderName = USER_VIDEO_FOLDER,
  onProgress,
}) {
  if (!payload || !jsonFileName) {
    throw new Error('Missing metadata for upload.');
  }
  if (!files || !files.left || !files.right) {
    throw new Error('Both left and right videos are required.');
  }

  const leftUpload = {
    key: `${folderName}/${payload.left.videoName}`,
    body: files.left,
    contentType: files.left.type || 'video/mp4',
    size: getFileSize(files.left),
  };
  const rightUpload = {
    key: `${folderName}/${payload.right.videoName}`,
    body: files.right,
    contentType: files.right.type || 'video/mp4',
    size: getFileSize(files.right),
  };
  const metadataBlob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const metadataUpload = {
    key: `${folderName}/${jsonFileName}`,
    body: metadataBlob,
    contentType: 'application/json',
    size: metadataBlob.size,
  };

  const STALL_MS = 60 * 1000; // 60 seconds

  async function verifyObjectSize(key, expectedSize) {
    try {
      const { results = [] } = await Storage.vault.list(key);
      const item = results.find((r) => r.key === key);
      return !!(item && typeof item.size === 'number' && item.size >= expectedSize);
    } catch (e) {
      return false;
    }
  }

  const runUpload = ({ key, body, contentType, size }) => {
    let fileLabel;
    if (key.includes('left')) fileLabel = 'Left video';
    else if (key.includes('right')) fileLabel = 'Right video';
    else fileLabel = 'Metadata JSON';

    return new Promise((resolve, reject) => {
      let lastLoaded = 0;
      let lastTick = Date.now();
      let ignore = false; // if we decide it stalled, ignore late callbacks

      // No-progress watchdog
      const watchdog = setInterval(() => {
        const idle = Date.now() - lastTick;
        if (idle > STALL_MS) {
          clearInterval(watchdog);
          ignore = true; // do not let late callbacks resolve this attempt
          reject(new Error(`Stalled while uploading ${fileLabel}`));
        }
      }, 5000);

      const progressCallback = (progress) => {
        if (ignore) return;
        const loaded = progress?.loaded || 0;
        if (loaded > lastLoaded) {
          lastLoaded = loaded;
          lastTick = Date.now();
        }
        const percentFile = Math.round((loaded / size) * 100);
        const visible = Math.min(percentFile, 99);
        const label =
          visible >= 99 ? `Finalizing upload for ${fileLabel}` : `Uploading ${fileLabel}`;
        if (typeof onProgress === 'function') onProgress(visible, label);
      };

      const completeCallback = (event) => {
        if (ignore) return;
        if (typeof onProgress === 'function') onProgress(100, `${fileLabel} completed`);
        clearInterval(watchdog);
        resolve(event);
      };

      const errorCallback = (err) => {
        if (ignore) return;
        console.error(`[Uploading] Error for: ${key}`, err);
        clearInterval(watchdog);
        reject(err);
      };

      // Kick off the upload
      Storage.vault.put(key, body, {
        contentType,
        resumable: true,
        progressCallback,
        completeCallback,
        errorCallback,
      });
    });
  };

  async function runUploadWithRetry(params, maxRetries = 1) {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await runUpload(params);
      } catch (err) {
        attempt += 1;
        // If the object actually made it despite our wrapper not seeing complete, treat it as success
        // eslint-disable-next-line no-await-in-loop
        const exists = await verifyObjectSize(params.key, params.size);
        if (exists) return { completed: true, retried: attempt > 0 };

        if (attempt > maxRetries) throw err;
      }
    }
  }

  try {
    // Upload both videos first, sequentially, with one retry on stall
    await runUploadWithRetry(leftUpload, 1);
    await runUploadWithRetry(rightUpload, 1);

    // Upload metadata last, usually very light
    await runUploadWithRetry(metadataUpload, 1);

    if (typeof onProgress === 'function') onProgress(100);

    return {
      leftKey: leftUpload.key,
      rightKey: rightUpload.key,
      metadataKey: metadataUpload.key,
    };
  } catch (error) {
    console.error(error);
    throw new Error('Unable to upload videos. Please try again.');
  }
}
